import { Icon20MessageOutline, Icon20ThumbsDown, Icon20ThumbsDownOutline, Icon20ThumbsUp, Icon20ThumbsUpOutline } from '@vkontakte/icons';
import { Spacing, Text, Textarea } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type RefObject, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAiPhotoGenerationAnalyticData } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.hooks';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { AiPhotoGenerationToolbarDelimiter } from 'reactApp/ui/AiPhotoGeneration/ToolbarComplete/Delimeter/AiPhotoGenerationToolbarDelimiter';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AiPhotoGenerationRatingBlock.css';

const MAX_SYMBOLS = 256;
const MESSAGE_SHOW_DELAY = 300;
const MIN_MESSAGE_LEN = 3;

type ResponseState = 'show' | 'message' | 'done' | null;

const dwhActions = {
    ratingShow: 'rating-show',
    ratingClick: 'rating-click',
    messageSend: 'message-send',
    messageClose: 'message-close',
};

// eslint-disable-next-line max-lines-per-function
export const AiPhotoGenerationRatingBlock = memo(function AiPhotoGenerationRatingBlock() {
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const analyticData = useAiPhotoGenerationAnalyticData();

    const [rating, setRating] = useState<boolean | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [responseState, setResponseState] = useState<ResponseState>(null);

    const isRatingUp = rating === true;
    const isRatingDown = rating === false;
    const isResponseButton = responseState === 'show';
    const isEditMessage = responseState === 'message';
    const isResponseSent = responseState === 'done';

    useEffect(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.ratingShow,
            ...analyticData,
            dwh_enabled_fields: [['paid_user', 'corp_user']],
        });
    }, []);

    useEffect(() => {
        let timerId;
        if (isRatingDown || isRatingUp) {
            timerId = setTimeout(() => setResponseState('show'), MESSAGE_SHOW_DELAY);
        }

        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [isRatingUp, isRatingDown]);

    const handleUp = useCallback(() => {
        if (isRatingDown) {
            return;
        }

        setRating(true);
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.ratingClick,
            button_name: 'rating_up',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'button_name']],
        });
    }, [analyticData, isRatingDown]);

    const handleDown = useCallback(() => {
        if (isRatingUp) {
            return;
        }

        setRating(false);
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.ratingClick,
            button_name: 'rating_down',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'button_name']],
        });
    }, [analyticData, isRatingUp]);

    const showMessageClick = useCallback(() => {
        if (isEditMessage) {
            return;
        }

        setResponseState('message');
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.ratingClick,
            button_name: 'message',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'button_name']],
        });
    }, [analyticData, isEditMessage]);

    const sendMessageClick = useCallback(() => {
        if (message.length < MIN_MESSAGE_LEN) {
            dispatch(
                showSnackbarAction({
                    id: 'ai-photo-generation-rating-fail',
                    type: SnackbarTypes.failure,
                    text: 'Интересно ваше мнение — поделитесь, пожалуйста',
                    closable: true,
                })
            );
            setIsError(true);
            return;
        }

        setResponseState('done');
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.messageSend,
            message: message.slice(0, MAX_SYMBOLS),
            rating: isRatingUp ? 'up' : 'down',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'rating']],
        });
        dispatch(
            showSnackbarAction({
                id: 'ai-photo-generation-rating-success',
                type: SnackbarTypes.success,
                text: 'Спасибо за ваше мнение',
                autoWidth: true,
                closable: true,
            })
        );
    }, [message, isRatingUp, analyticData, dispatch]);

    const onChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        setIsError(false);
    }, []);

    const onEnterPress = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.code === 'Enter') {
                event.preventDefault();

                sendMessageClick();
            }
        },
        [sendMessageClick]
    );

    const onTooltipClose = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: dwhActions.messageClose,
            message: message.slice(0, MAX_SYMBOLS),
            rating: isRatingUp ? 'up' : 'down',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user']],
        });
        setMessage('');
        setIsError(false);
        setResponseState('show');
    }, [analyticData, isRatingUp, message]);

    const handleShowMessageForm = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'message-show',
            ...analyticData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user']],
        });
    }, [analyticData]);

    if (isResponseSent) {
        return <div className={classNames(styles.ratingWrapper, styles.ratingWrapper_none)} />;
    }

    if (isResponseButton || isEditMessage) {
        return (
            <>
                <div className={classNames(styles.ratingWrapper, styles.ratingWrapper_long)}>
                    <div
                        className={classNames(styles.button, { [styles.button_active]: isEditMessage })}
                        onClick={showMessageClick}
                        data-qa-id="send-message-bttn"
                        ref={ref}
                    >
                        <Icon20MessageOutline data-qa-id="message-icon" />
                        Оставьте отзыв
                    </div>
                    {isEditMessage && (
                        <FloatingTooltip
                            onClose={onTooltipClose}
                            target={ref as RefObject<HTMLDivElement>}
                            placement={ETooltipPlacement.topStart}
                            qaId="send-message-ai-photo-generation"
                            appearance={ETooltipAppearance.dark}
                            className={styles.tooltipRoot}
                            leftOffset={-82}
                            closable
                            usePortal
                            hideArrow
                            closeOnOutsideClick={true}
                            autoUpdateOnResize
                            allowFlip
                            onShow={handleShowMessageForm}
                        >
                            <div className={styles.tooltip}>
                                <Text className={styles.tooltipText}>Как&nbsp;вам&nbsp;создание изображения с&nbsp;нейросетью?</Text>
                                <Spacing size={16} />
                                <Textarea
                                    data-qa-id="ai-resp-text"
                                    className={styles.tooltipEdit}
                                    placeholder="Напишите, что понравилось, а что можно улучшить"
                                    value={message}
                                    onChange={onChange}
                                    onKeyPress={onEnterPress}
                                    rows={3}
                                    maxHeight={80}
                                    maxLength={MAX_SYMBOLS}
                                    status={isError ? 'error' : undefined}
                                />
                                <Spacing size={8} />
                                <div className={styles.counter}>
                                    {message.length}/{MAX_SYMBOLS}
                                </div>
                                <Spacing size={20} />
                                <Button
                                    theme="vk"
                                    sizeMode={ButtonSizeMode.small}
                                    className={styles.tooltipButton}
                                    onClick={sendMessageClick}
                                    primary
                                >
                                    Отправить
                                </Button>
                            </div>
                        </FloatingTooltip>
                    )}
                </div>
                <AiPhotoGenerationToolbarDelimiter />
            </>
        );
    }

    return (
        <>
            <div className={styles.ratingWrapper}>
                <div className={styles.ratingBlock}>
                    <div
                        className={classNames(styles.button, styles.button_icon, { [styles.button_active]: isRatingUp })}
                        data-qa-id="rating-block"
                        onClick={handleUp}
                    >
                        <Icon20ThumbsUpOutline
                            data-qa-id="rating-up"
                            className={classNames(styles.icon, { [styles.iconActive]: !isRatingUp })}
                        />
                        <Icon20ThumbsUp
                            data-qa-id="rating-up-active"
                            className={classNames(styles.icon, { [styles.iconActive]: isRatingUp })}
                        />
                    </div>
                    <div
                        className={classNames(styles.button, styles.button_icon, { [styles.button_active]: isRatingDown })}
                        data-qa-id="rating-block"
                        onClick={handleDown}
                    >
                        <Icon20ThumbsDownOutline
                            data-qa-id="rating-down"
                            className={classNames(styles.icon, { [styles.iconActive]: !isRatingDown })}
                        />
                        <Icon20ThumbsDown
                            data-qa-id="rating-down-active"
                            className={classNames(styles.icon, { [styles.iconActive]: isRatingDown })}
                        />
                    </div>
                </div>
            </div>
            <AiPhotoGenerationToolbarDelimiter />
        </>
    );
});

AiPhotoGenerationRatingBlock.displayName = 'AiPhotoGenerationRatingBlock';
